import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { uniqueId } from 'lodash';
import { filesize } from 'filesize';
import Dropzone from 'react-dropzone';
import { FiArrowLeft, FiX, FiUpload, FiHeadphones } from 'react-icons/fi';

import { Permissao, Status, StatusAssinatura } from 'utils/variables';
import { show } from 'store/modules/dialog/actions';
import api from 'services/api';

import Card from 'components/Card';
import Button from 'components/Button';
import Input from 'components/Input';
import Checkbox from 'components/Checkbox';
import Spinner from 'components/Spinner';
import TextArea from 'components/TextArea';
import DatePicker from 'components/DatePicker';
import PlayerBaixa from 'components/PlayerBaixa';

import {
  Container,
  Content,
  Form,
  ButtonUpload,
  ButtonUploadError,
  FormContent,
  FormActions,
  DropContainer,
  Upload,
  Image,
  ColumnContent,
  ThumbnailContainer,
  CircularContainer,
  FileContent,
} from './styles';

import nomusic from 'assets/nomusic.png';
import music from 'assets/music.png';
import { FormHandles } from '@unform/core';
import { SpotRadio, Radio, SpotRadioAssinatura } from 'pages/@types';
import getValidationErrors from 'services/getValidationErros';
import AppError from 'utils/AppError';
import { useModal } from 'hooks/modal';
import { usePermit } from 'utils/hooks/usePermit';
import Select from 'components/Select';

interface RadioOptions {
  id: number;
  label: string;
}

interface AssinaturaOptions {
  value: number;
  label: string;
}
// configuração do validator
const schema = Yup.object().shape({
  retranca: Yup.string()
    .required('Campo Obrigatório')
    .matches(/^[a-zA-Z0-9- ./]*$/, 'Retranca contém caracteres especiais')
    .max(26, 'Retranca excedeu o limite de 26 caracteres'),
  duracao: Yup.number()
    .typeError('Duração deve ser um número')
    .min(3)
    .required('Campo Obrigatório'),
});
const schemaAprovado = Yup.object().shape({
  retranca: Yup.string()
    .required('Campo Obrigatório')
    .matches(/^[a-zA-Z0-9- ./]*$/, 'Retranca contém caracteres especiais')
    .max(26, 'Retranca excedeu o limite de 26 caracteres'),
  duracao: Yup.number()
    .typeError('Duração deve ser um número')
    .min(3, 'O spot deve durar pelo menos 3s')
    .required('Campo Obrigatório'),
  radios: Yup.string().required('Escolha pelo menos uma rádio'),
  datainicio: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data início inválida'),
  datafim: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data fim inválida'),
});

export default function SpotRadioStore() {
  // react-router-dom
  const navigate = useNavigate();

  // redux
  const dispatch = useDispatch();
  const hasAvaliador = usePermit([], [Permissao.AVALIADOR]);

  /**
   * USE STATE
   */
  const [radios, setRadios] = useState<RadioOptions[]>([]);
  const [assinaturas, setAssinaturas] = useState<AssinaturaOptions[]>([]);
  const [todas, setTodas] = useState(false);
  const [status, setStatus] = useState('');
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [obsReprova, setObsReprova] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [audioFile, setAudioFile] = useState<any[]>([]);
  const [fileURL, setFileURL] = useState('');
  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });
  const [initialData, setInitialData] = useState({} as SpotRadio);
  const { showModal, hideModal } = useModal();

  /**
   * USE PARAMS
   */
  const { spotId } = useParams<{ spotId: string }>();

  /**
   * USE REF
   */
  const formRef = useRef<FormHandles>(null);

  /**
   * USE CALLBACK
   */
  const updateFile = useCallback((file, data) => {
    setAudioFile([{ ...file, ...data }]);
  }, []);

  const handleSelectedRadio = useCallback(() => {
    if (
      formRef.current &&
      formRef.current.getFieldValue('radios').length === radios.length
    )
      setTodas(true);
  }, [radios.length]);

  const handleTodas = useCallback(
    e => {
      e.preventDefault();

      if (formRef.current) {
        if (formRef.current.getFieldValue('radios').length === radios.length) {
          formRef.current.clearField('radios');
          setTodas(false);
        } else {
          formRef.current.setFieldValue('radios', radios.map(r => r.id).join());
          setTodas(true);
        }
      }
    },
    [radios],
  );

  /**
   * FUNÇÕES
   */
  const closeVideo = useCallback(() => {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }, [player.open]);

  const handlePlayer = useCallback(
    (e, previewurl) => {
      e.preventDefault();
      if (previewurl) {
        setPlayer({
          open: !player.open,
          midia_url: previewurl,
          pipe: false,
        });
      } else {
        dispatch(
          show({
            type: 'warning',
            text: 'Preview indisponível no momento',
            showConfirmButton: false,
          }),
        );
      }
    },
    [dispatch, player.open],
  );

  const handleSubmitObsAvaliacao = useCallback(
    async data => {
      if (formRef.current) {
        setLoadingSubmit(true);
        try {
          formRef.current.setErrors({});

          const schema = Yup.object().shape({
            obsreprova: Yup.string()
              .required('Preencha a observação')
              .max(800, 'A observação excede o limite de 800 caracteres'),
          });

          await schema.validate({ ...data }, { abortEarly: false });

          setObsReprova(data.obsreprova);

          if (formRef.current) formRef.current.submitForm();

          hideModal();
        } catch (error) {
          if (error instanceof Yup.ValidationError) {
            const errors = getValidationErrors(error);
            formRef.current.setErrors(errors);
            return;
          }
          if (error instanceof Error) {
            toast.error(error.message);
          }
        }
        setLoadingSubmit(false);
      }
    },
    [hideModal],
  );

  const renderObsAvaliacao = useCallback(() => {
    return (
      <Form ref={formRef} onSubmit={handleSubmitObsAvaliacao}>
        <TextArea
          placeholder="Observação"
          name="obsreprova"
          height={150}
          disable={false}
          errorField=""
          icon=""
        />
        <div className="actions">
          <Button color="default" onClick={() => hideModal()}>
            CANCELAR
          </Button>
          <Button type="submit" color="primary">
            CONFIRMAR
          </Button>
        </div>
      </Form>
    );
  }, [handleSubmitObsAvaliacao, hideModal]);

  const handleAvaliacao = useCallback(
    (e, status) => {
      e.preventDefault();
      setStatus(status);
      showModal({
        title: 'OBSERVAÇÕES',
        component: renderObsAvaliacao(),
        width: '30vw',
      });
    },
    [renderObsAvaliacao, showModal],
  );

  function renderDragButton(
    isDragActive: boolean,
    isDragReject: boolean,
    hasMidia = false,
  ) {
    if (!isDragActive) {
      return (
        <>
          <ButtonUpload hasMidia={hasMidia} onClick={e => e.preventDefault()}>
            <FiUpload />
            <p> {hasMidia ? 'Substituir mídia' : 'Arraste ou clique'}</p>
          </ButtonUpload>
        </>
      );
    }
    if (isDragReject) {
      return (
        <ButtonUploadError hasMidia={hasMidia}>
          <FiX /> <p>Formato não permitido</p>
        </ButtonUploadError>
      );
    }
    return (
      <ButtonUpload hasMidia={hasMidia}>
        <FiUpload /> <p>Solte o arquivo</p>
      </ButtonUpload>
    );
  }

  const handleSuccessMessage = useCallback(
    ({ contentHTML, redirectTo }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleDragReject = useCallback(
    files => {
      if (files) {
        const ext = files[0].name.split('.')[1];
        const text = `Não é permitido o envio de arquivo com extensão .${ext}`;

        dispatch(show({ type: 'error', text }));
      }
    },
    [dispatch],
  );

  const processUpload = useCallback(
    selectedFile => {
      if (selectedFile) {
        const data = new FormData();
        data.append('file', selectedFile.file);
        api
          .patch(`/spotradiofile/${spotId}`, data, {
            timeout: 180000 * 3,
            onUploadProgress: e => {
              const progress = e.total
                ? Math.round((e.loaded * 100) / e.total)
                : 0;
              updateFile(selectedFile, {
                progress,
              });
            },
          })
          .then(response => {
            updateFile(selectedFile, {
              uploaded: true,
              progress: 100,
              error: false,
            });
            setTimeout(() => setAudioFile([]), 1000);
            setInitialData({
              ...initialData,
              filename: selectedFile.file.path,
            });
            toast.success('Mídia carregada com sucesso');
            setFileURL(response.data.filepath_url);
          })
          .catch(err => {
            updateFile(selectedFile.id, {
              error: true,
              progress: 0,
              uploaded: false,
            });
            setTimeout(() => setAudioFile([]), 1000);
            if (err.status === 400) {
              const optionBase = {
                type: 'error',
                text: err.message,
              };
              dispatch(show(optionBase));
            }
            if (err.status === 406) {
              const optionBase = {
                type: 'error',
                text: err.message,
              };
              dispatch(show(optionBase));
            }
          });
      }
    },
    [dispatch, initialData, spotId, updateFile],
  );

  const handleUpload = useCallback(
    files => {
      const filesFormatted = files.map((file: any) => ({
        file,
        id: uniqueId(),
        name: file.name,
        readableSize: filesize(file.size),
        progress: 0,
        uploaded: false,
        preview: URL.createObjectURL(file),
        error: false,
      }));

      setAudioFile([...filesFormatted]);

      filesFormatted.map((file: any) => processUpload(file));
    },
    [processUpload],
  );

  const handleSubmit = useCallback(
    async data => {
      try {
        setLoadingSubmit(true);
        if (formRef.current) formRef.current.setErrors({});

        await (status === Status.APROVADO
          ? schemaAprovado
          : schema
        ).validate(data, { abortEarly: false });

        const payload = {
          ...data,
          ...(status && {
            status,
            obsreprova: obsReprova || null,
          }),
          observacao: data.observacao || null,
        };

        if (spotId) {
          const response = await api.put(`/spotradio/${spotId}`, payload);
          if (response.data) {
            handleSuccessMessage({
              contentHTML:
                '<h3>Spot atualizado!</h3><p>Você será redirecionado para os Spots em 3 segundos</p>',
              redirectTo: `/spotradio`,
            });
          } else {
            toast.error('Falha de comunicação com o sistema');
          }
        } else {
          const response = await api.post('/spotradio', payload);
          if (response.data) {
            handleSuccessMessage({
              contentHTML:
                '<h3>Spot criado!</h3><p>Você será redirecionado para os Spots em 3 segundos</p>',
              redirectTo: `/spotradio/${response.data.id}`,
            });
          } else {
            toast.error('Falha de comunicação com o sistema');
          }
        }

        setLoadingSubmit(false);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
        setLoadingSubmit(false);
      }
    },
    [spotId, handleSuccessMessage, status, obsReprova],
  );

  /**
   * USE EFFECT
   */
  useEffect(() => {
    async function loadSpot() {
      setLoading(true);
      try {
        const response = await api.get(`/spotradio/${spotId}`);
        setInitialData({
          ...response.data,
          spotradio_assinatura: response.data.spotradio_assinatura && {
            value: response.data.spotradio_assinatura.id,
            label: response.data.spotradio_assinatura.titulo,
          },
          datainicio: response.data.datainicio
            ? new Date(response.data.datainicio)
            : null,
          datafim: response.data.datafim
            ? new Date(response.data.datafim)
            : null,
          radios: response.data.radios
            ? response.data.radios.map((r: Radio) => r.id)
            : [],
        });
        setStatus(response.data.status);
        setFileURL(response.data.filepath_url);
        setLoading(false);
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else console.log(error);
        // toast.error(err.message);s
        setLoading(false);
      }
    }
    // Verifica se é edição
    if (spotId) {
      loadSpot();
    }
  }, [spotId]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadRadios() {
      setLoadingOptions(true);
      try {
        const [responseRadio, responseAssinatura] = await Promise.all([
          api.get('/radio'),
          api.get('/spotradio/assinatura'),
        ]);

        if (responseRadio?.data && responseAssinatura?.data) {
          setRadios(
            responseRadio.data.map((r: Radio) => {
              return { id: r.id, label: r.nome };
            }),
          );
          setAssinaturas(
            responseAssinatura.data.map((a: SpotRadioAssinatura) => ({
              value: a.id,
              label: a.titulo,
            })),
          );
        } else toast.error('Falha de comunicação com o sistema');
        setLoadingOptions(false);
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoadingOptions(false);
    }
    loadRadios();
    return () => {
      controller.abort();
    };
  }, []);

  /**
   * USE MEMO
   */

  const inUpload = useMemo(
    () =>
      !!audioFile &&
      audioFile.length > 0 &&
      !audioFile[0].uploaded &&
      !audioFile[0].error &&
      audioFile[0].progress > 0,

    [audioFile],
  );

  return (
    <Container>
      <header>
        <Button
          color="primary"
          onClick={e => {
            e.preventDefault();
            navigate(-1);
          }}
        >
          <FiArrowLeft />
          VOLTAR
        </Button>
      </header>
      <Card>
        <header>
          <h4>ADICIONAR NOVO SPOT</h4>
        </header>
        <Content>
          {!loading && !loadingOptions && (
            <Form
              ref={formRef}
              initialData={initialData}
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              {/* <Scroll height="calc(100vh - 280px)"> */}
              <div>
                <FormContent>
                  <ColumnContent columns="1fr 1fr">
                    <div>
                      <section>
                        <h5>Dados gerais: </h5>
                      </section>
                      <div>
                        <Input
                          name="retranca"
                          placeholder="Retranca *"
                          disabled={
                            loadingSubmit ||
                            loadingOptions ||
                            initialData.status === Status.APROVADO
                          }
                        />

                        <Input
                          name="duracao"
                          type="number"
                          placeholder="Duração *"
                          disabled={
                            loadingSubmit ||
                            loadingOptions ||
                            initialData.status === Status.APROVADO
                          }
                        />
                      </div>
                      <div>
                        <section>
                          <h5>Período de veiculação:</h5>
                        </section>
                        <ColumnContent columns="1fr 1fr 4fr">
                          <DatePicker
                            placeholderText="DE"
                            minDate={new Date()}
                            name="datainicio"
                            disabled={
                              loadingSubmit ||
                              loadingOptions ||
                              initialData.status === Status.APROVADO
                            }
                          />

                          <DatePicker
                            placeholderText="ATÉ"
                            minDate={new Date()}
                            name="datafim"
                            disabled={
                              loadingSubmit ||
                              loadingOptions ||
                              initialData.status === Status.APROVADO
                            }
                          />
                        </ColumnContent>
                      </div>
                      <div>
                        <section>
                          <h5>Assinaturas</h5>
                        </section>
                        <Select
                          name="spotradio_assinatura"
                          options={assinaturas}
                          placeholder="Assinaturas"
                          disabled={
                            !!(
                              spotId &&
                              (initialData.statusassinatura !==
                                StatusAssinatura.NA ||
                                initialData.status === Status.APROVADO)
                            )
                          }
                        />
                      </div>
                    </div>
                    {spotId && (
                      <FileContent>
                        <Upload>
                          <Dropzone
                            accept={{
                              'audio/mpeg': ['.mp3'],
                            }}
                            multiple={false}
                            onDropRejected={handleDragReject}
                            onDropAccepted={handleUpload}
                            disabled={['aprovado', 'aprovacao'].includes(
                              initialData.status,
                            )}
                          >
                            {({
                              getRootProps,
                              getInputProps,
                              isDragActive,
                              isDragReject,
                            }) => (
                              <DropContainer
                                {...getRootProps()}
                                isDragActive={isDragActive}
                                isDragReject={isDragReject}
                              >
                                {!inUpload && <input {...getInputProps()} />}
                                <ThumbnailContainer
                                  hasMidia={!!initialData.filename}
                                  inUpload={!!inUpload}
                                >
                                  <Image
                                    onClick={() => console.log('OI')}
                                    src=""
                                    loader={<Spinner />}
                                    unloader={
                                      <img
                                        src={
                                          initialData.filename ? music : nomusic
                                        }
                                        alt="sem thumb"
                                      />
                                    }
                                  />
                                  {!inUpload &&
                                    !['aprovado', 'aprovacao'].includes(
                                      initialData.status,
                                    ) &&
                                    renderDragButton(
                                      isDragActive,
                                      isDragReject,
                                      !!initialData.filename,
                                    )}
                                </ThumbnailContainer>
                              </DropContainer>
                            )}
                          </Dropzone>

                          {!!audioFile && audioFile.length > 0 && (
                            <CircularContainer
                              styles={{
                                root: { width: 124 },
                                path: { stroke: '#3358f4' },
                                text: {
                                  fill: '#3358f4',
                                  fontSize: '22px',
                                  fontWeight: 600,
                                },
                              }}
                              text={`${audioFile[0].progress}%`}
                              strokeWidth={5}
                              value={audioFile[0].progress}
                            />
                          )}
                        </Upload>
                        {fileURL && (
                          <Button
                            className="headphones"
                            id="id_escutar"
                            fill
                            color="font"
                            round
                            // disabled={!sp.filepath_url}
                            onClick={e => handlePlayer(e, fileURL)}
                          >
                            <FiHeadphones />
                          </Button>
                        )}
                      </FileContent>
                    )}
                  </ColumnContent>
                  <ColumnContent columns="1fr">
                    <div>
                      <section>
                        <h5>Rádios:</h5>

                        <Button
                          fill
                          color="primary"
                          size="xsm"
                          onClick={e => handleTodas(e)}
                        >
                          TODAS ? {todas ? 'SIM' : 'NÃO'}
                        </Button>
                      </section>

                      <Checkbox
                        name="radios"
                        options={radios}
                        disabled={
                          loadingSubmit ||
                          loadingOptions ||
                          initialData.status === Status.APROVADO
                        }
                        handleSelected={handleSelectedRadio}
                      />
                    </div>
                    <section>
                      <h5>Observação:</h5>
                    </section>
                    <TextArea
                      name="observacao"
                      height="calc(100vh - 700px)"
                      placeholder="Mensagem que será exibida no email"
                      icon=""
                      errorField=""
                      disabled={
                        loadingSubmit ||
                        loadingOptions ||
                        initialData.status === Status.APROVADO
                      }
                    />
                  </ColumnContent>
                </FormContent>
              </div>
              <FormActions>
                {([Status.EMPROCESSO, Status.ALTERACAO].includes(
                  initialData.status,
                ) ||
                  !initialData.status) && (
                  <>
                    <Button
                      type="submit"
                      disabled={
                        loadingSubmit ||
                        loadingOptions ||
                        initialData.status === Status.APROVADO
                      }
                      color="primary"
                    >
                      {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => setStatus(Status.APROVACAO)}
                      disabled={
                        loadingSubmit ||
                        loadingOptions ||
                        !initialData.filename ||
                        (initialData.statusassinatura !==
                          StatusAssinatura.PROCESSADO &&
                          initialData.statusassinatura !== StatusAssinatura.NA)
                      }
                    >
                      {!loadingSubmit ? (
                        ' ENVIAR PARA APROVAÇÃO'
                      ) : (
                        <Spinner type="ThreeDots" />
                      )}
                    </Button>
                  </>
                )}
                {initialData.status === Status.APROVACAO &&
                  (hasAvaliador ? (
                    <>
                      <Button
                        color="danger"
                        disabled={loadingSubmit}
                        onClick={e => handleAvaliacao(e, Status.ALTERACAO)}
                      >
                        {!loadingSubmit ? (
                          'REPROVAR'
                        ) : (
                          <Spinner type="ThreeDots" />
                        )}
                      </Button>
                      <Button
                        // type="submit"
                        color="success"
                        disabled={loadingSubmit}
                        onClick={() => setStatus(Status.APROVADO)}
                      >
                        {!loadingSubmit ? (
                          'APROVAR'
                        ) : (
                          <Spinner type="ThreeDots" />
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      color="danger"
                      type="submit"
                      disabled={loadingSubmit}
                      onClick={() => setStatus(Status.ALTERACAO)}
                    >
                      {!loadingSubmit ? (
                        'VOLTAR PARA ALTERAÇÃO'
                      ) : (
                        <Spinner type="ThreeDots" />
                      )}
                    </Button>
                  ))}
              </FormActions>
            </Form>
          )}
        </Content>
      </Card>
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
        type="audio"
      />
    </Container>
  );
}
